import { QueryKey, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import { createApiInstance } from '@/pages/Insights/Context/ApiContext';
import { UseInsightsApiQueryOptions } from '@/pages/Insights/hooks/useInsightsQuery';
import { BottlenecksData } from '@/pages/Insights/pages/InsightsBottlenecks/InsightsBottlenecks.types';

export type InsightsDetails = {
  bottlenecksCount: number;
  bottlenecksUrl: string;
  duration: {
    increasedBy: number;
    url: string;
  };
  credits: {
    increasedBy: number;
    url: string;
  };
};

type InsightsDetailsProps = {
  accountSlug?: string;
  projectId: string;
  projectSlug: string;
  triggeredWorkflow: string;
  traceToken?: string;
};

const useInsightsApiQueryWithoutContext = <ResponseType, ErrorType = unknown>(
  queryKey: QueryKey,
  url: string,
  traceToken: string,
  options?: UseInsightsApiQueryOptions<ResponseType, ErrorType>,
): UseQueryResult<ResponseType, AxiosError<ErrorType>> => {
  const api = useMemo(() => createApiInstance(traceToken), [traceToken]);
  return useQuery({
    queryKey,
    queryFn: () => api.get(url),
    select: (response) => response.data,
    ...options,
  });
};

const useInsightsDetails = ({
  accountSlug,
  projectId,
  projectSlug,
  triggeredWorkflow,
  traceToken,
}: InsightsDetailsProps): InsightsDetails => {
  const { data } = useInsightsApiQueryWithoutContext<BottlenecksData>(
    ['bottlenecks', projectSlug, projectId],
    `/trace/metric-api/bottlenecks?app_slug=${projectSlug}&project_id=${projectId}&workflow=${triggeredWorkflow}`,
    traceToken ?? '',
    {
      enabled: !!traceToken,
    },
  );

  let bottlenecksCount = 0;
  if (data) {
    bottlenecksCount += Object.values(data?.builds || {}).flat().length;
    bottlenecksCount += Object.values(data?.credits || {}).flat().length;
    bottlenecksCount += Object.values(data?.tests || {}).flat().length;
  }

  return {
    bottlenecksCount,
    bottlenecksUrl: accountSlug
      ? `/insights/${accountSlug}/bottlenecks?app_slug=${projectSlug}&project_id=${projectId}&workflow=${triggeredWorkflow}&ref=build-details-workflow-bottlenecks`
      : '',
    duration: {
      increasedBy: data?.builds.build_time_v1?.[0]?.trend_pct || 0,
      url: accountSlug
        ? `/insights/${accountSlug}/explore/builds?interval=daily&duration=P30D&tab=time_success_p50&app_slug=${projectSlug}&workflow=${triggeredWorkflow}&ref=build-details-view-trends`
        : '',
    },
    credits: {
      increasedBy: data?.credits?.build_time_credits_v1?.[0]?.trend_pct || 0,
      url: accountSlug
        ? `/insights/${accountSlug}/explore/utilization?app_slug=${projectSlug}&workflow=${triggeredWorkflow}&timeWindowMode=calendar&ref=build-details-view-credits`
        : '',
    },
  };
};

export default useInsightsDetails;
